import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARKETING_OPTIONS = ["Google Ads", "SEO agency", "Social media", "Email marketing", "None", "Other"];
const GOALS = [
  { key: "more_phone_calls", label: "More phone calls" },
  { key: "more_website_visits", label: "More website visits" },
  { key: "more_bookings", label: "More bookings" },
  { key: "just_want_to_know", label: "Just want to know where I stand" },
];
const DELIVERY = [
  { key: "email_pdf", label: "Email PDF" },
  { key: "live_call", label: "Live walkthrough call" },
  { key: "both", label: "Both — PDF + call" },
];

/** Public AI Visibility Audit intake form. Magic-link authenticated. */
export default function IntakeAIVisibility() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    business_name: "",
    website_url: "",
    service_area: "",
    industry: "",
    competitors: ["", "", ""],
    customer_questions: "",
    current_marketing: [],
    google_business_claimed: false,
    primary_goal: "more_phone_calls",
    report_delivery: "email_pdf",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/intakes/public/${token}`);
        setSession(data);
        setIndustries(data.industries || []);
        if (data.intake?.answers && Object.keys(data.intake.answers).length) {
          const a = data.intake.answers;
          setForm((f) => ({
            ...f,
            ...a,
            competitors: (a.competitors || []).concat(["", "", ""]).slice(0, 3),
          }));
        } else if (data.intake?.business_name) {
          setForm((f) => ({ ...f, business_name: data.intake.business_name }));
        }
      } catch (err) {
        setError(err.response?.data?.detail || "This audit link is invalid or expired. Ask your Clients OnRamp contact for a new one.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const toggleMarketing = (opt) => {
    setForm((f) => ({
      ...f,
      current_marketing: f.current_marketing.includes(opt)
        ? f.current_marketing.filter((x) => x !== opt)
        : [...f.current_marketing, opt],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.customer_questions.trim().length < 10) {
      return toast.error("Please list at least a few of the questions your customers ask.");
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/intakes/public/${token}/submit`, form);
      setDone(true);
      toast.success("Thanks! Your audit is being prepared.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const readOnly = useMemo(() => session?.read_only || done, [session, done]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE]"><Loader2 className="w-8 h-8 animate-spin text-[#E24E1B]" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE] p-8">
        <div className="max-w-md text-center bg-white border border-neutral-200 p-8">
          <h1 className="font-display text-3xl font-bold mb-3">Link unavailable</h1>
          <p className="text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }
  if (done || readOnly) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EE] p-8">
        <div className="max-w-md text-center bg-white border border-neutral-200 p-8" data-testid="intake-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold mb-3">Got it — thanks!</h1>
          <p className="text-neutral-600">We&apos;ve received your intake and our team is preparing your AI Visibility Audit. You&apos;ll hear from us within 1-2 business days.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] py-10 px-4" data-testid="intake-ai-visibility-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-2xl mx-auto">
        <header className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#E24E1B] font-semibold mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> AI Visibility Audit · intake
          </div>
          <h1 className="font-display text-4xl font-bold">3 minutes → your audit</h1>
          <p className="text-neutral-600 mt-2">
            Tell us a bit about your business and your customers. We&apos;ll turn it into a personalized report showing exactly where you stand in AI + Google search — and what to fix first.
          </p>
        </header>

        <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 space-y-6">
          <Field label="Business name" required>
            <input required maxLength={200} value={form.business_name}
                   onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                   data-testid="intake-business-name" className="lf-input" />
          </Field>

          <Field label="Website URL" required
                 hint="Paste your current website. If you don't have one yet, write 'none'.">
            <input required maxLength={400} value={form.website_url}
                   onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                   data-testid="intake-website-url" className="lf-input"
                   placeholder="https://example.com or 'none'" />
          </Field>

          <Field label="City / service area" required
                 hint="What city or region do you serve?">
            <input required maxLength={200} value={form.service_area}
                   onChange={(e) => setForm({ ...form, service_area: e.target.value })}
                   data-testid="intake-service-area" className="lf-input"
                   placeholder="e.g. Los Angeles, CA — LA County" />
          </Field>

          <Field label="Industry / category" required>
            <select required value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    data-testid="intake-industry" className="lf-input">
              <option value="" disabled>Choose one…</option>
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              <option value="Other">Other</option>
            </select>
          </Field>

          <Field label="Top 3 competitors"
                 hint="Who are your biggest local competitors? Name or website is fine.">
            {[0, 1, 2].map((i) => (
              <input key={i} maxLength={200} value={form.competitors[i] || ""}
                     onChange={(e) => {
                       const next = [...form.competitors];
                       next[i] = e.target.value;
                       setForm({ ...form, competitors: next });
                     }}
                     data-testid={`intake-competitor-${i}`}
                     className="lf-input mb-2"
                     placeholder={`Competitor #${i + 1}`} />
            ))}
          </Field>

          <Field label="What questions do customers actually ask you?" required
                 hint="List 5 questions customers typically ask before choosing a business like yours. Example: 'best emergency plumber near me', 'how much does X cost'">
            <textarea required rows={6} maxLength={3000} value={form.customer_questions}
                      onChange={(e) => setForm({ ...form, customer_questions: e.target.value })}
                      data-testid="intake-customer-questions" className="lf-input"
                      placeholder="One per line, or comma-separated." />
          </Field>

          <Field label="Current marketing" hint="Select any that apply (optional).">
            <div className="grid grid-cols-2 gap-2">
              {MARKETING_OPTIONS.map((opt) => (
                <label key={opt} data-testid={`intake-marketing-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                       className={`text-sm border p-2 cursor-pointer transition ${form.current_marketing.includes(opt) ? "border-[#E24E1B] bg-orange-50" : "border-neutral-200"}`}>
                  <input type="checkbox" checked={form.current_marketing.includes(opt)}
                         onChange={() => toggleMarketing(opt)} className="mr-2" />
                  {opt}
                </label>
              ))}
            </div>
          </Field>

          <Field label="Google Business Profile claimed?">
            <div className="flex gap-2">
              {[["yes", true], ["no", false]].map(([label, val]) => (
                <button key={label} type="button"
                        onClick={() => setForm({ ...form, google_business_claimed: val })}
                        data-testid={`intake-gbp-${label}`}
                        className={`flex-1 border px-4 py-2 text-sm ${form.google_business_claimed === val ? "border-[#E24E1B] bg-orange-50 font-semibold" : "border-neutral-200"}`}>
                  {label.toUpperCase()}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Primary goal for this audit" required>
            <div className="grid gap-2">
              {GOALS.map((g) => (
                <label key={g.key}
                       data-testid={`intake-goal-${g.key}`}
                       className={`text-sm border p-3 cursor-pointer transition ${form.primary_goal === g.key ? "border-[#E24E1B] bg-orange-50 font-semibold" : "border-neutral-200"}`}>
                  <input type="radio" name="primary_goal" checked={form.primary_goal === g.key}
                         onChange={() => setForm({ ...form, primary_goal: g.key })} className="mr-2" />
                  {g.label}
                </label>
              ))}
            </div>
          </Field>

          <Field label="Preferred report delivery">
            <div className="grid gap-2">
              {DELIVERY.map((d) => (
                <label key={d.key}
                       data-testid={`intake-delivery-${d.key}`}
                       className={`text-sm border p-3 cursor-pointer transition ${form.report_delivery === d.key ? "border-[#E24E1B] bg-orange-50 font-semibold" : "border-neutral-200"}`}>
                  <input type="radio" name="report_delivery" checked={form.report_delivery === d.key}
                         onChange={() => setForm({ ...form, report_delivery: d.key })} className="mr-2" />
                  {d.label}
                </label>
              ))}
            </div>
          </Field>

          <button type="submit" disabled={submitting}
                  data-testid="intake-submit"
                  className="w-full bg-[#0F2340] text-white font-semibold py-3 hover:bg-[#E24E1B] transition disabled:opacity-50">
            {submitting ? "Sending…" : "Submit intake →"}
          </button>
        </form>

        <footer className="text-center text-xs text-neutral-500 mt-6">
          © Clients OnRamp — this link is private to your business. Do not share.
        </footer>
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1">
        {label}{required && <span className="text-[#E24E1B] ml-1">*</span>}
      </span>
      {hint && <span className="block text-xs text-neutral-500 mb-2">{hint}</span>}
      {children}
    </label>
  );
}
